<!-- eslint-disable vue/no-v-html -->
<template>
  <ContentSection
    spacing="none"
    class="pb-16 transition-[height] sm:pb-28"
    :class="{ 'pt-12 sm:pt-18': index > 0 }"
  >
    <LocationsFinderFilter
      v-model="filter"
      :location-types="locationTypesList"
      :loading="loading"
    />
    <div ref="placesServiceDiv" class="hidden" /> <!-- Hidden div to initialize the PlacesService -->
    <GoogleMap
      ref="mapRef"
      map-id="locations-finder-map"
      :api-key="config.public.GMAPS_KEY"
      :center="center"
      :zoom="mapDefaultZoom"
      class="relative h-[calc(100vh-var(--navbar-height)-8rem)] max-h-216 min-h-144 w-full"
      disable-default-ui
      zoom-control
    >
      <!-- LOCATION MARKERS -->
      <AdvancedMarker
        v-for="marker, markerIndex in markers"
        :key="marker.id"
        :ref="(marker) => markerRefs[markerIndex] = marker"
        :options="marker.options"
        :pin-options="marker.pinOptions"
        @click="handleMarkerClick(marker)"
      />
      <!-- SEARCH LOCATION MARKER -->
      <AdvancedMarker
        v-if="searchLocationMarker"
        :options="searchLocationMarker.options"
        :pin-options="searchLocationMarker.pinOptions"
      />

      <LocationInfo
        v-if="selectedLocation"
        class="
          absolute top-16 z-10 max-h-[calc(100%-8rem)] w-108 overflow-y-auto rounded-100 p-12
          lg:right-container-desktop-half
          max-lg:hidden
          xl:right-container-desktop
        "
        :location="selectedLocation"
        @show-contact-people="showContactPeople()"
      />
      <Transition name="fade-in">
        <div
          v-if="!filteredLocations?.length"
          class="absolute inset-0 flex items-center justify-center bg-black/80"
        >
          <NoEntriesFound class="text-white" />
        </div>
      </Transition>
    </GoogleMap>
    <Container>
      <LocationInfo
        v-if="selectedLocation"
        class="
          mx-auto max-w-108 pt-12
          lg:hidden
          md:max-w-144
          xs:relative xs:z-10 xs:-mt-16 xs:p-12 xs:shadow-100
        "
        :location="selectedLocation"
        @show-contact-people="showContactPeople()"
      />
    </Container>
    <div ref="teamMembersRef" /><!-- this is to scroll to scroll the the switch between the map and the team members to the center of the screen  -->
    <Container use-full-width class="mt-16">
      <div v-show="contactPeopleVisible">
        <TransitionGroup
          v-if="peoplePerLocations?.length"
          tag="ul"
          name="list"
          class=""
        >
          <li v-for="location in peoplePerLocations" :key="location.id">
            <h3 class="mb-9 font-semibold typo-h3">
              {{ location.title }}
            </h3>
            <ul
              class="
                flex flex-col gap-x-default gap-y-default
                sm:grid sm:grid-cols-[repeat(auto-fill,_minmax(17.5rem,_1fr))]
              "
            >
              <li v-for="teamMember in location.teamMembers" :key="teamMember.id">
                <CardTeamMember :data="teamMember" />
              </li>
            </ul>
          </li>
        </TransitionGroup>
        <!-- NO RESULTS -->
        <NoEntriesFound v-show="!peoplePerLocations?.length && !loading" class="py-9 sm:pb-16 sm:pt-24" />
        <!-- LOADING -->
        <div v-show="loading" class="flex w-full items-center justify-center py-9 sm:pb-16 sm:pt-24">
          <IconSpin class="size-12 animate-spin text-primary-900" />
        </div>
      </div>
    </Container>
  </ContentSection>
</template>

<script setup lang="ts">
import { AdvancedMarker, GoogleMap } from 'vue3-google-map'
import type { MainComponentsLocationsFinderFragment, LocationFragment, TeamMemberFragment } from '#gql'

export type LocationFilterType = {
  locationType: string | null
  searchLocation: string
  searchTeamMember: string
}

type SearchLocationDataType = {
  name: string | undefined
  lat: number | undefined
  lng: number | undefined
}

const config = useRuntimeConfig()

const props = defineProps<{
  data: Enforce<MainComponentsLocationsFinderFragment>
  index: number
}>()

const { t, locale } = useI18n()
const route = useRoute()

const contactPeopleVisible = ref(false)
const teamMembersRef = ref<HTMLElement | null>(null)
const teamMembers = ref<TeamMemberFragment[]>([])
const searchLocationData = ref<SearchLocationDataType[]>([])

const mapRef = ref<GoogleMapInstance | null>(null)
const markerRefs = ref<Element[] | ComponentPublicInstance[] | null[]>([])
const placesServiceDiv = ref<HTMLDivElement | null>(null)
const selectedLocation = ref<LocationFragment | null>(null)
const loading = ref(false)

/** *** *** *** *** */
/** *** FILTER  *** */
/** *** *** *** *** */
const filter = ref<LocationFilterType>({
  locationType: route.query.type ? (route.query.type as string) : 'all',
  searchLocation: route.query.location ? (route.query.location as string) : '',
  searchTeamMember: route.query.team_member ? (route.query.team_member as string) : '',
})

const locationTypesList = computed(() => { // list of fields of application to populate filter
  const sortedLocationType = props.data.location_types
    .filter(x => x.__typename === 'Term_LocationTypes_LocationTypes')
    .toSorted((a, b) => +a.index - +b.index)

  return [
    {
      id: 'all',
      slug: 'all',
      title: t('all'),
    },
    {
      id: 'headquarter',
      slug: 'headquarter',
      title: t('headquarter'),
    },
    ...sortedLocationType.map(location_type => ({
      id: location_type.id,
      slug: location_type.slug,
      title: location_type.title,
    })),
  ]
})

// Filtering
const filteredLocations = computed(() => {
  let filterResult = props.data.entries

  const { locationType, searchLocation, searchTeamMember } = filter.value

  // LOCATION TYPE
  if (locationType !== 'all') {
    if (locationType === 'headquarter') {
      filterResult = filterResult.filter((location: LocationFragment) => location.is_headquarter === true)
    } else {
      filterResult = filterResult.filter((location: LocationFragment) => location.location_type?.slug === locationType)
    }
  }

  // SEARCH LOCATION
  if (searchLocation !== '' && searchLocationData.value.length > 0) {
    const searchLatLng = `${searchLocationData.value[0]?.lat}, ${searchLocationData.value[0]?.lng}`

    filterResult = filterResult.toSorted((a, b) => {
      const aDistance = calculateEuclideanDistance(a.lat_lng, searchLatLng)
      const bDistance = calculateEuclideanDistance(b.lat_lng, searchLatLng)
      return aDistance - bDistance
    }).slice(0, 3)
  }

  // SEARCH TEAM MEMBER FILTER
  if (searchTeamMember !== '' && teamMembers.value.length > 0) {
    // Extract location IDs from the team members array
    const locationIds = new Set(
      teamMembers.value
        .filter(member => member.location !== null)
        .map(member => (member.location as LocationFragment).id),
    )

    // Filter the filterResult array
    filterResult = filterResult.filter(location => locationIds.has(location.id))
  }

  return filterResult
})

// watch for filter changes
watch( // all filter changes
  () => filter.value,
  () => {
    saveFilterInUrl()
  },
  { deep: true },
)

watch(
  () => filter.value.locationType,
  () => {
    contactPeopleVisible.value = false
    if (filter.value.searchTeamMember.length >= 3) {
      fetchTeamMembers()
    }
    if (filter.value.locationType === 'headquarter') {
      selectedLocation.value = filteredLocations.value.find(location => location.is_headquarter === true) ?? null
    }
  },
)

watch(
  () => filter.value.searchLocation,
  () => {
    contactPeopleVisible.value = false
    getSearchLocationData()
    filter.value.searchTeamMember = ''
  },
)

let searchTeamMemberDebounceTimeout: NodeJS.Timeout | null = null
watch(
  () => filter.value.searchTeamMember,
  () => {
    if (searchTeamMemberDebounceTimeout) {
      clearTimeout(searchTeamMemberDebounceTimeout)
    }
    filter.value.searchLocation = ''
    searchTeamMemberDebounceTimeout = setTimeout(() => {
      if (filter.value.searchTeamMember.length >= 3) {
        contactPeopleVisible.value = true
        selectedLocation.value = null
        teamMembers.value = []
        fetchTeamMembers()
      } else {
        contactPeopleVisible.value = false
        teamMembers.value = []
      }
    }, 500)
  },
)

// watch for team members changes
watch(
  () => teamMembers.value,
  () => {
    if (teamMembers.value.length === 0) {
      // contactPeopleVisible.value = false
    } else {
      // contactPeopleVisible.value = true
    }
  },
)

// Update selectedLocation outside of the computed property
watch(filteredLocations, (newLocations) => {
  // If the previously selected location is no longer in the filtered list, set it to null
  if (!newLocations.some(location => location.id === selectedLocation.value?.id)) {
    selectedLocation.value = null
  }
  manageBounds()
})

// Saves the filter in the URL
const router = useRouter()
function saveFilterInUrl() {
  router.push({
    query: {
      type: filter.value.locationType,
      location: filter.value.searchLocation,
      team_member: filter.value.searchTeamMember,
    },
  })
}

/** *** *** *** */
/** *** MAP *** */
/** *** *** *** */
// define map data
interface GoogleMapInstance {
  map: google.maps.Map
  ready: boolean
}

let placesService: google.maps.places.PlacesService | null = null

const center = {
  lat: 47.44681176111253,
  lng: 8.603942343173598,
} // Initial center -> wetrok hq
const mapDefaultZoom = 10

// define markers with filtered locations
const markers = computed(() => {
  return filteredLocations.value
    .filter(location => location.lat_lng)
    .map((location) => {
      // Split the coordinates string into latitude and longitude
      const [loc_lat, loc_long] = location.lat_lng.split(',').map(coord => coord.trim())

      // Return the new object with the position property
      return {
        id: location.id,
        options: {
          position: {
            lat: parseFloat(loc_lat),
            lng: parseFloat(loc_long),
          },
        },
        pinOptions: {
          background: location.id === selectedLocation.value?.id ? '#007A97' : '',
          glyphColor: location.id === selectedLocation.value?.id ? '#FFFFFF' : '',
          borderColor: location.id === selectedLocation.value?.id ? '#007A97' : '',
        },
      }
    })
})

const searchLocationMarker = computed(() => {
  if (!searchLocationData.value || searchLocationData.value.length === 0 || searchLocationData.value[0].lat === undefined || searchLocationData.value[0].lng === undefined) {
    return false
  }

  return {
    options: {
      position: {
        lat: searchLocationData.value[0].lat,
        lng: searchLocationData.value[0].lng,
      },
    },
    pinOptions: {
      background: '#EA9A00',
      glyphColor: '#541C4D',
      borderColor: '#541C4D',
      scale: 1.25,
    },
  }
})

// zoom to show all markers
function manageBounds() {
  if (mapRef.value && mapRef.value.map && window.google) {
    const bounds = new window.google.maps.LatLngBounds()

    if (markers.value.length === 0) {
      // if no markers, set the center of the map to the default center
      mapRef.value.map.setZoom(mapDefaultZoom)
      mapRef.value.map.setCenter(center)
      return
    }

    if (markers.value.length === 1) {
      // if only one marker, set the center of the map to the marker
      mapRef.value.map.setZoom(mapDefaultZoom)
      mapRef.value.map.setCenter(markers.value[0].options.position)
      return
    }

    markers.value.forEach((marker) => {
      bounds.extend(marker.options.position)
    })

    if (searchLocationMarker.value) {
      bounds.extend(searchLocationMarker.value.options.position)
    }

    const padding = 50
    mapRef.value.map.fitBounds(bounds, {
      left: padding,
      top: padding,
      right: padding,
      bottom: padding,
    })
  }
}

// init places service
function initPlacesService() {
  if (mapRef.value && mapRef.value.ready && placesServiceDiv.value) {
    placesService = new google.maps.places.PlacesService(placesServiceDiv.value)
  }
}

// watch for map ready
watch(() => mapRef.value?.ready, (ready) => {
  if (ready) {
    initPlacesService()
    if (filter.value.searchLocation.length > 0) {
      getSearchLocationData()
    }
    manageBounds()
  }
})

function handleMarkerClick(marker: {
  id: string
}) {
  contactPeopleVisible.value = false
  selectedLocation.value = filteredLocations.value.find(location => location.id === marker.id) ?? null
}

async function getSearchLocationData() {
  if (!placesService) {
    console.error('Places Service is not initialized yet.')
    return
  }

  if (filter.value.searchLocation.length === 0) {
    searchLocationData.value = []
    return
  }

  const request = {
    query: filter.value.searchLocation,
  }

  placesService.textSearch(request, (results, status) => {
    if (status === google.maps.places.PlacesServiceStatus.OK && results) {
      searchLocationData.value = results.map(place => ({
        name: place.name,
        lat: place.geometry?.location?.lat(),
        lng: place.geometry?.location?.lng(),
      }))
    } else {
      console.error('Text search failed:', status)
      searchLocationData.value = [] // Clear results on failure
    }
  })
}

function calculateEuclideanDistance(lat_lng1: string, lat_lng2: string) {
  const [lat1, lng1] = lat_lng1.split(',').map(coord => parseFloat(coord.trim()))
  const [lat2, lng2] = lat_lng2.split(',').map(coord => parseFloat(coord.trim()))
  const distance = Math.sqrt(Math.pow(lat2 - lat1, 2) + Math.pow(lng2 - lng1, 2))
  return distance
}

/** *** *** *** *** *** *** */
/** *** CONTACT PERSON  *** */
/** *** *** *** *** *** *** */
const peoplePerLocations = computed(() => {
  const locationMap = new Map()

  teamMembers.value.forEach((member) => {
    const { location } = member
    if (location !== undefined && location !== null) {
      if (!locationMap.has((location as LocationFragment).id)) {
        locationMap.set((location as LocationFragment).id, {
          ...location,
          teamMembers: [],
        })
      }

      locationMap.get((location as LocationFragment).id).teamMembers.push({
        id: member.id,
        image: member.image,
        title: member.title,
        function: member.function,
        email: member.email,
        phone: member.phone,
      })
    }
  })

  return Array.from(locationMap.values())
})

async function showContactPeople() {
  teamMembers.value = []
  contactPeopleVisible.value = true
  fetchTeamMembers()
  teamMembersRef?.value?.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  })
}

const GqlInstance = useGql()
async function fetchTeamMembers() {
  const filterTeamMember = {
    title: { contains: filter.value.searchTeamMember },
  }

  const locationIds = selectedLocation.value ? [selectedLocation.value.id] : filteredLocations.value.map(location => location.id)

  loading.value = true

  const payload = await GqlInstance('TeamMembers', {
    sort: 'order asc',
    site: locale.value,
    filter: filterTeamMember,
    locations: locationIds,
  })

  loading.value = false

  if (!payload?.entries?.data) {
    console.error('TeamMembers could not be fetched')
    return
  }

  teamMembers.value = payload.entries.data as TeamMemberFragment[]
}

onMounted(() => {
  if (filter.value.searchTeamMember.length >= 3) {
    contactPeopleVisible.value = true
    fetchTeamMembers()
  } else {
    if (filter.value.locationType === 'all' || filter.value.searchLocation === '') {
      selectedLocation.value = filteredLocations.value.find(location => location.is_headquarter === true) ?? null
    }
  }
})
</script>
